import axios from 'axios';

import {
  CalDavCalendar,
  CalDavCalendarsUpdateRequest,
  CalDavConfigCreate,
  CalDavConfigResponse,
  CalDavStatusResponse,
  CalDavSyncResult,
  CalDavValidationError,
} from '@/features/caldav/types';
import type { CalendarEventWithColor, EventAttendee } from '@/features/calendarView/types';

import { apiUrls } from './apiUrls';
import { createApiInstance } from './createApiInstance';
import { ItemResponse, ItemsResponse } from './types';

export class CalDavApiError extends Error {
  code: string;

  constructor(message: string, code: string) {
    super(message);
    this.code = code;
    this.name = 'CalDavApiError';
  }
}

class CalDavApi {
  private axiosInstance = createApiInstance('caldav', {
    onUnauthorized: () => {
      window.location.href = '/login';
    },
  });

  async getStatus(): Promise<CalDavStatusResponse> {
    const response = await this.axiosInstance.get<ItemResponse<CalDavStatusResponse>>(
      apiUrls.integrations.caldav.status,
    );

    return response.data.item;
  }

  async getConfig(): Promise<CalDavConfigResponse | null> {
    try {
      const response = await this.axiosInstance.get<ItemResponse<CalDavConfigResponse>>(
        apiUrls.integrations.caldav.config,
      );

      return response.data.item;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return null;
      }

      throw error;
    }
  }

  async saveConfig(data: CalDavConfigCreate): Promise<CalDavConfigResponse> {
    try {
      const response = await this.axiosInstance.post<ItemResponse<CalDavConfigResponse>>(
        apiUrls.integrations.caldav.config,
        data,
      );

      return response.data.item;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const validationError = error.response.data?.detail as CalDavValidationError;

        if (validationError?.code) {
          throw new CalDavApiError(validationError.detail, validationError.code);
        }
      }

      throw error;
    }
  }

  async deleteConfig(): Promise<void> {
    await this.axiosInstance.delete(apiUrls.integrations.caldav.config);
  }

  async getCalendars(): Promise<CalDavCalendar[]> {
    try {
      const response = await this.axiosInstance.get<ItemsResponse<CalDavCalendar>>(
        apiUrls.integrations.caldav.calendars,
      );

      return response.data.items;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 404) {
        return [];
      }

      throw error;
    }
  }

  async syncCalendars(): Promise<CalDavCalendar[]> {
    try {
      const response = await this.axiosInstance.post<ItemsResponse<CalDavCalendar>>(
        apiUrls.integrations.caldav.syncCalendars,
      );

      return response.data.items;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const validationError = error.response.data?.detail as CalDavValidationError;

        if (validationError?.code) {
          throw new CalDavApiError(validationError.detail, validationError.code);
        }
      }

      throw error;
    }
  }

  async updateCalendars(data: CalDavCalendarsUpdateRequest): Promise<CalDavCalendar[]> {
    const response = await this.axiosInstance.patch<ItemsResponse<CalDavCalendar>>(
      apiUrls.integrations.caldav.calendars,
      data,
    );

    return response.data.items;
  }

  async unlinkWorklog(eventId: string): Promise<CalendarEventWithColor> {
    const response = await this.axiosInstance.delete<ItemResponse<CalendarEventWithColor>>(
      `${apiUrls.integrations.caldav.events}/${eventId}/worklog`,
    );

    return response.data.item;
  }

  async syncEvents(): Promise<CalDavSyncResult> {
    try {
      const response = await this.axiosInstance.post<ItemResponse<CalDavSyncResult>>(
        apiUrls.integrations.caldav.syncEvents,
      );

      return response.data.item;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const validationError = error.response.data?.detail as CalDavValidationError;

        if (validationError?.code) {
          throw new CalDavApiError(validationError.detail, validationError.code);
        }
      }

      throw error;
    }
  }

  async getEventAttendees(eventId: string): Promise<EventAttendee[]> {
    const response = await this.axiosInstance.get<ItemsResponse<EventAttendee>>(
      apiUrls.integrations.caldav.eventAttendees(eventId),
    );
    return response.data.items;
  }

  async getEvents(from: string, to: string): Promise<CalendarEventWithColor[]> {
    try {
      const response = await this.axiosInstance.get<ItemsResponse<CalendarEventWithColor>>(
        apiUrls.integrations.caldav.events,
        { params: { from, to } },
      );

      return response.data.items;
    } catch (error) {
      if (axios.isAxiosError(error) && error.response?.status === 400) {
        const errorData = error.response.data as { extra?: { code?: string } };

        if (errorData.extra?.code === 'caldav_not_configured') {
          throw new CalDavApiError('CalDAV not configured', 'caldav_not_configured');
        }
      }

      throw error;
    }
  }
}

export const caldavApi = new CalDavApi();
